/**
 * @file The only system allowed to mutate health.
 *
 * Everything that hurts something emits a `damage` event; this system applies
 * it, honours invulnerability windows, drives the hit reaction and announces
 * deaths. Centralising it means armour, shields or damage types later become a
 * change in one place.
 *
 * It also owns the player's kill countdown (issue #20): the player carries no
 * hit points, only seconds. They drain every frame, damage burns extra seconds,
 * and every kill refills them. Reaching zero dies through the very same
 * `Events.DIED` path as a hit-point death, so audio, particles and the game
 * over transition need to know nothing about it.
 */

import { System } from '../ecs/System.js';
import {
  Health,
  Transform,
  PlayerControlled,
  StatusEffects,
  TimedStatus,
  timedStatusRemaining,
  Destructible
} from '../components/index.js';
import { getPlayerTuning } from '../config/PlayerTuning.js';
import { Events } from '../core/EventBus.js';
// Sandbox cheats are read live, the same pattern the wave director uses for
// the sandbox enemy toggles: debug state stays in one place and gameplay
// systems consult it instead of being reconfigured.
import { sandboxState } from '../sandbox/SandboxConfig.js';
import { clamp } from '../utils/MathUtils.js';

/** Peak extra scale applied to a body the instant it is hit. */
const HIT_PUNCH = 0.3;
/** How quickly the hit punch decays, in approach units per second. */
const HIT_PUNCH_DECAY = 14;

export class HealthSystem extends System {
  static priority = 70;

  constructor() {
    super();
    /** @type {(() => void)[]} @private */
    this._subscriptions = [];
    /** @type {number} Last countdown tenth announced, throttles the HUD event. @private */
    this._announcedTenths = -1;
    /**
     * Reused confirmed-damage payload. Every landed hit emits one — automatic
     * fire and shotgun pellets make that a hot path — so it follows the same
     * scratch-object rule as the emitters upstream of it.
     * @type {object} @private
     */
    this._appliedEvent = {
      target: null,
      amount: 0,
      source: null,
      type: undefined,
      direction: null,
      impulse: 0
    };
    /** @type {object} Reused camera-trauma payload. @private */
    this._shakeEvent = { amount: 0 };
  }

  /** @returns {void} */
  init() {
    this._subscriptions.push(
      this.ctx.events.on(Events.DAMAGE, (payload) => this._applyDamage(payload)),
      this.ctx.events.on(Events.DIED, (payload) => this._onDied(payload))
    );
  }

  /**
   * @param {{ target: import('../ecs/Entity.js').Entity, amount: number,
   *           source: import('../ecs/Entity.js').Entity | null, type?: string }} payload
   *   Damage event. `type` (`'explosion' | 'bullet' | 'melee'`) is optional;
   *   destructible props use it to shrug off damage they are immune to.
   * @returns {void}
   * @private
   */
  _applyDamage({ target, amount, source, type, direction = null, impulse = 0 }) {
    if (!target?.alive) return;
    const health = target.get(Health);
    if (!health || health.dead || health.invulnerableFor > 0 || amount <= 0) return;

    // A destructible only reacts to the damage types its row lists; a crate that
    // is bullet-proof simply ignores the shot rather than absorbing it.
    const destructible = target.get(Destructible);
    if (destructible && !destructible.acceptsDamageType(type)) return;

    // Sandbox cheats: god mode shrugs off anything aimed at the player, and
    // one-shot kills turn any player-sourced hit lethal for everything else.
    if (sandboxState.cheats.godMode && target.has(PlayerControlled)) return;
    if (sandboxState.cheats.immortal && target.has(PlayerControlled)) return;
    if (target.has(PlayerControlled) && timedStatusRemaining(target, TimedStatus.IMMORTAL) > 0) return;
    if (
      sandboxState.cheats.oneShotKills &&
      source?.has?.(PlayerControlled) &&
      !target.has(PlayerControlled)
    ) {
      amount = Number.MAX_SAFE_INTEGER;
    }

    const incoming = this._absorbWithShield(target, amount);
    // A hit the shield swallowed whole costs nothing — not hit points, not
    // countdown seconds, and no immunity window either: i-frames exist to stop
    // real damage from stacking, and a fully-absorbed hit dealt none.
    if (incoming <= 0) return;

    // The immunity window after a real hit. For the player it is read live so
    // the sandbox can tune survivability without a respawn; everything else
    // keeps the window baked onto its Health at spawn.
    const isPlayer = target.has(PlayerControlled);
    health.invulnerableFor = isPlayer ? getPlayerTuning().invulnerabilityTime : health.invulnerabilityTime;
    health.timeSinceHit = 0;

    // Past every guard: this hit is real. Anything that reacts to a hit landing
    // rather than to a hit being requested hangs off here (issue #75).
    this._appliedEvent.target = target;
    this._appliedEvent.amount = incoming;
    this._appliedEvent.source = source ?? null;
    this._appliedEvent.type = type;
    this._appliedEvent.direction = direction;
    this._appliedEvent.impulse = impulse;
    this.ctx.events.emit(Events.DAMAGE_APPLIED, this._appliedEvent);

    if (health.usesKillTimer) {
      // Damage is paid in seconds. Hitting zero here is deliberately *not*
      // death: `update` owns that check, so a kill landing later in the same
      // frame can still buy the player out of it.
      health.killTimer = Math.max(0, health.killTimer - incoming * health.killTimerDamagePenalty);
      if (isPlayer) {
        this._announceKillTimer(health);
        this._emitPlayerHitFeedback();
      }
      return;
    }

    health.current = clamp(health.current - incoming, 0, health.max);

    if (isPlayer) {
      this._emitPlayerHitFeedback();
    }

    if (health.current > 0) return;

    health.dead = true;
    this.ctx.events.emit(Events.SHAKE, { amount: this.ctx.config.feel.trauma.death });
    // Announce first, destroy second: listeners need a live entity to read.
    this.ctx.events.emit(Events.DIED, { entity: target, source: source ?? null });
    this.world.destroyEntity(target);
  }

  /**
   * Camera shake for a hit the player really took (issue #74). Called from the
   * accepted-damage branches only, which is what keeps shielded, invulnerable and
   * god-moded hits silent. The HUD's screen flash rides the same guard by way of
   * `DAMAGE_APPLIED`, so there is no second player-damage event to keep in step.
   *
   * The numbers come through {@link getPlayerTuning} rather than the frozen
   * config so the sandbox's Player section can tune them without a respawn.
   *
   * @returns {void}
   * @private
   */
  _emitPlayerHitFeedback() {
    this._shakeEvent.amount = getPlayerTuning().damageFeedback.shake;
    this.ctx.events.emit(Events.SHAKE, this._shakeEvent);
  }

  /**
   * A confirmed kill refills the killer's countdown. Filtering `Events.DIED` on
   * its `source` keeps this to one rule in one place — no separate "kill" event
   * for every damage dealer to remember to emit.
   *
   * @param {{ entity: import('../ecs/Entity.js').Entity,
   *           source: import('../ecs/Entity.js').Entity | null }} payload Death event.
   * @returns {void}
   * @private
   */
  _onDied({ entity, source }) {
    if (!source || source === entity || !source.alive || !source.has(PlayerControlled)) return;

    const health = source.get(Health);
    if (!health?.usesKillTimer || health.dead) return;

    health.killTimer = health.killTimerMax;
    this._announceKillTimer(health);
  }

  /**
   * Pushes the countdown to the HUD, but only when the tenth of a second it
   * displays actually changes — roughly ten events a second instead of one per
   * frame.
   *
   * @param {import('../components/Health.js').Health} health Player health.
   * @returns {void}
   * @private
   */
  _announceKillTimer(health) {
    const tenths = Math.ceil(health.killTimer * 10);
    if (tenths === this._announcedTenths) return;
    this._announcedTenths = tenths;

    this.ctx.events.emit(Events.PLAYER_KILLTIMER_CHANGED, {
      current: health.killTimer,
      max: health.killTimerMax
    });
  }

  /**
   * Spends shield charge before health. Shields are a `StatusEffects` field, so
   * anything can carry one — the rule lives here and only here.
   *
   * @param {import('../ecs/Entity.js').Entity} target Entity being hurt.
   * @param {number} amount Incoming damage.
   * @returns {number} Damage left over for the health pool.
   * @private
   */
  _absorbWithShield(target, amount) {
    const effects = target.get(StatusEffects);
    if (!effects || effects.shield <= 0) return amount;

    const absorbed = Math.min(effects.shield, amount);
    effects.shield -= absorbed;
    if (effects.shield <= 0) effects.shieldRemaining = 0;

    this.ctx.events.emit(Events.STATUS_CHANGED, { effects });
    return amount - absorbed;
  }

  /**
   * @param {number} dt Delta time in seconds.
   * @returns {void}
   */
  update(dt) {
    /** @type {import('../ecs/Entity.js').Entity | null} Countdown that ran dry. */
    let expired = null;

    for (const entity of this.world.query(Health)) {
      const health = entity.get(Health);
      if (health.invulnerableFor > 0) health.invulnerableFor -= dt;
      health.timeSinceHit += dt;

      if (health.usesKillTimer && !health.dead) {
        // God mode freezes the countdown: only the player uses the timer.
        const immortal = entity.has(PlayerControlled) && timedStatusRemaining(entity, TimedStatus.IMMORTAL) > 0;
        if (!sandboxState.cheats.godMode && !sandboxState.cheats.pulseFrozen && !immortal) {
          health.killTimer = Math.max(0, health.killTimer - dt);
        }
        this._announceKillTimer(health);
        if (health.killTimer <= 0) expired = entity;
      }

      // Hit reaction: a quick scale punch that decays. Cheap, and it works with
      // shared materials, unlike a per-entity colour flash.
      const transform = entity.get(Transform);
      if (transform) {
        transform.scale = 1 + HIT_PUNCH * Math.exp(-health.timeSinceHit * HIT_PUNCH_DECAY);
      }
    }

    // Deferred out of the loop: a `died` listener that touches the world would
    // rebuild the cached query array being iterated.
    if (expired) this._killByTimer(expired);
  }

  /**
   * Death by countdown. Same three announcements as a hit-point death, with no
   * killer to credit.
   *
   * @param {import('../ecs/Entity.js').Entity} entity Entity whose timer ran out.
   * @returns {void}
   * @private
   */
  _killByTimer(entity) {
    const health = entity.get(Health);
    health.dead = true;
    this.ctx.events.emit(Events.SHAKE, { amount: this.ctx.config.feel.trauma.death });
    this.ctx.events.emit(Events.DIED, { entity, source: null });
    this.world.destroyEntity(entity);
  }

  /** @returns {void} */
  dispose() {
    for (const unsubscribe of this._subscriptions) unsubscribe();
    this._subscriptions.length = 0;
  }
}
